import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const STATUS_PILL = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  trialing: { label: "Trialing", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  past_due: { label: "Past due", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  grace_period: { label: "Grace period", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  unpaid: { label: "Unpaid", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  canceled: { label: "Canceled", cls: "bg-slate-200 text-slate-700 border-slate-300" },
  incomplete: { label: "Incomplete", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function formatCents(cents) {
  return "$" + (cents / 100).toFixed(2);
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
}

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [placeholderMode, setPlaceholderMode] = useState(false);
  const schemeId = getActiveSchemeId();

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      // Detect placeholder mode from /health so the banner shows on initial load
      try {
        const h = await api.get("/health");
        if (h.data?.integrations?.stripe_placeholder_mode) setPlaceholderMode(true);
      } catch { /* health is non-critical */ }
      const { data } = await api.get(`/schemes/${schemeId}/billing`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (schemeId) reload(); /* eslint-disable-next-line */ }, [schemeId]);

  const onSubscribe = async () => {
    setSubmitting(true);
    setPlaceholderMode(false);
    try {
      const { data } = await api.post(`/schemes/${schemeId}/billing/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "stripe_key_placeholder") {
        setPlaceholderMode(true);
        toast.error("Stripe placeholder key — see banner below");
      } else {
        toast.error(typeof detail === "string" ? detail : "Checkout failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onPortal = async () => {
    try {
      const { data } = await api.post(`/schemes/${schemeId}/billing/portal`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal session failed");
    }
  };

  if (!schemeId) return <AppShell><div className="p-8 text-slate-600">No active scheme. Open the dashboard first.</div></AppShell>;

  const sub = data?.subscription;
  const status = sub?.status || (data?.stripe_configured ? "not_configured" : "not_configured");
  const pill = STATUS_PILL[status] || { label: "Not configured", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  const monthlyTotal = data ? formatCents(data.monthly_total_cents) : "—";

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="billing-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Billing</h1>
            <p className="text-sm text-slate-600 mt-1">Manage your Opstrait Committee subscription.</p>
          </div>
          <Badge data-testid="billing-status-pill" className={`${pill.cls} border px-3 py-1 text-xs font-medium`}>{pill.label}</Badge>
        </div>

        {placeholderMode && (
          <Card className="border-amber-300 bg-amber-50" data-testid="billing-stripe-placeholder-banner">
            <CardContent className="py-4 text-sm text-amber-900 leading-relaxed space-y-2">
              <div className="font-semibold">Stripe live key required</div>
              <div>Subscription, Customer Portal, webhook, and lot-quantity sync are all code-complete and will activate immediately once a real Stripe test key is in place.</div>
              <ol className="list-decimal pl-5 text-xs space-y-1">
                <li>Get a key from <span className="font-mono">https://dashboard.stripe.com/test/apikeys</span> (starts with <span className="font-mono">sk_test_51…</span>).</li>
                <li>In <span className="font-mono">/app/backend/.env</span>, set <span className="font-mono">STRIPE_API_KEY=sk_test_51…</span> and <span className="font-mono">STRIPE_WEBHOOK_SECRET=whsec_…</span> (from Stripe Dashboard → Webhooks, endpoint <span className="font-mono">/api/webhook/stripe</span>).</li>
                <li>Run <span className="font-mono">sudo supervisorctl restart backend</span> — done.</li>
              </ol>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card><CardContent className="py-8"><Skeleton className="h-24 w-full" /></CardContent></Card>
        ) : error ? (
          <Card className="border-rose-300 bg-rose-50"><CardContent className="py-4 text-sm text-rose-700">{String(error)}</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="text-base">Subscription</CardTitle></CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Lots covered</div>
                    <div data-testid="billing-lot-count" className="text-2xl font-semibold text-slate-900">{data.lot_count}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Monthly total</div>
                    <div data-testid="billing-monthly-total" className="text-2xl font-semibold text-[#0066ff]">
                      {monthlyTotal} <span className="text-xs text-slate-500 font-normal">AUD</span>
                    </div>
                  </div>
                </div>
                {sub && (
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Current period</div>
                      <div data-testid="billing-period" className="text-slate-700">{formatDate(sub.current_period_start)} → {formatDate(sub.current_period_end)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Next invoice</div>
                      <div data-testid="billing-next-invoice" className="text-slate-700">{formatDate(sub.current_period_end)}</div>
                    </div>
                    {sub.trial_end && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trial ends</div>
                        <div className="text-slate-700">{formatDate(sub.trial_end)}</div>
                      </div>
                    )}
                    {sub.grace_period_until && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Grace period until</div>
                        <div className="text-amber-700 font-medium">{formatDate(sub.grace_period_until)}</div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-2">
                  {(!sub || sub.status === "canceled" || sub.status === "incomplete") ? (
                    <Button data-testid="billing-subscribe-btn" onClick={onSubscribe} disabled={submitting} className="bg-[#0066ff] hover:bg-[#0052cc]">
                      {submitting ? "Opening Checkout…" : "Subscribe"}
                    </Button>
                  ) : (
                    <Button data-testid="billing-portal-btn" onClick={onPortal} className="bg-slate-900 hover:bg-slate-800">
                      Manage in Customer Portal
                    </Button>
                  )}
                  {sub && sub.stripe_subscription_id && (
                    <Button data-testid="billing-sync-btn" variant="outline" onClick={async () => {
                      try { await api.post(`/schemes/${schemeId}/billing/sync`); toast.success("Synced"); reload(); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
                    }}>Re-sync from Stripe</Button>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">What's covered</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm text-slate-700 leading-relaxed">
                <p><span className="font-semibold">$5 AUD per lot</span> per month.</p>
                <p>14-day free trial on first subscribe.</p>
                <p>Cancel anytime via the Customer Portal.</p>
                <p>Lot count syncs to Stripe automatically when lots are added or removed.</p>
                <p className="pt-2 border-t border-slate-100 text-xs text-slate-500">
                  Owners are never affected by chair payment failures — RSVPs, votes, messages, and Q&A stay open during grace period.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        <p className="text-xs text-slate-400 pt-2">Billing managed by Stripe. {data?.stripe_configured ? null : <span className="text-amber-700">Stripe is not configured on the server — see banner above.</span>}</p>
      </div>
    </AppShell>
  );
}
